"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "@/lib/router";
import { AlertCircle } from "lucide-react";
import ChartSwitcher from "@/components/blocks/chart-switcher";
import type { Symbol, TimeFrame } from "@/store/trade/use-binary-store";
import { useTranslations } from "@/hooks/use-translations";
import type { MarketMetadata } from "@/lib/trade/precision-utils";
import { useChartData } from "./chart-data-context";
import { marketDataWs, type OHLCVData } from "@/services/trade/market-data-ws";

interface ChartPanelProps {
  symbol: Symbol;
  onPriceUpdate?: (price: number) => void;
  metadata?: MarketMetadata;
  marketType?: string;
}

export default function ChartPanel({ symbol, onPriceUpdate, metadata, marketType: propMarketType }: ChartPanelProps) {
  const t = useTranslations("trade/components/chart/chart-panel");
  const [localCurrentPrice, setLocalCurrentPrice] = useState<number>(0);
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeFrame>("1h");
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [chartKey, setChartKey] = useState<string>(`chart-${symbol}`);
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") || "spot";
  const urlMarketType = type === "futures" ? "futures" : "spot";
  // Use prop when provided (parent detects eco tokens), else fall back to URL
  const marketType = (propMarketType as any) || urlMarketType;
  const ohlcvUnsubscribeRef = useRef<(() => void) | null>(null);

  // Get chart data context to share data with other panels
  const { setCandleData, setChartType, setDataReady, setCurrentPrice } = useChartData();

  // Handle price updates from the chart
  useEffect(() => {
    if (localCurrentPrice > 0 && onPriceUpdate) {
      onPriceUpdate(localCurrentPrice);
    }
    // Share current price with chart data context
    setCurrentPrice(localCurrentPrice);
  }, [localCurrentPrice, onPriceUpdate, setCurrentPrice]);

  // Handle chart context ready
  const handleChartContextReady = (context: any) => {
    console.log('📊 Chart Context Ready:', {
      hasContext: !!context,
      candleDataLength: context?.candleData?.length || 0,
      chartType: context?.chartType,
      currentPrice: context?.currentPrice
    });

    if (
      context &&
      typeof context.currentPrice === "number" &&
      context.currentPrice > 0
    ) {
      setLocalCurrentPrice(context.currentPrice);
    }

    // Share chart data with context if available
    if (context && context.candleData && Array.isArray(context.candleData) && context.candleData.length > 0) {
      console.log('✅ Setting candle data:', context.candleData.length, 'candles');
      setCandleData(context.candleData);
      setDataReady(true);
    } else {
      console.warn('⚠️ No candle data in context:', {
        hasContext: !!context,
        hasCandleData: !!context?.candleData,
        isArray: Array.isArray(context?.candleData),
        length: context?.candleData?.length || 0
      });
      setDataReady(false);
    }

    // Share chart type with context if available
    if (context && context.chartType) {
      setChartType(context.chartType);
    }
  };

  // Handle symbol changes and force chart remount
  useEffect(() => {
    // Reset layout ready state when symbol changes to force remount
    setIsLayoutReady(false);

    // Update chart key to force complete remount
    setChartKey(`chart-${symbol}-${Date.now()}`);

    // Reset current price for new symbol
    setLocalCurrentPrice(0);

    // Clear chart data context to prevent showing old data for new symbol
    setCandleData([]);
    setDataReady(false);
    setCurrentPrice(0);

    // Unsubscribe from previous OHLCV subscription
    if (ohlcvUnsubscribeRef.current) {
      ohlcvUnsubscribeRef.current();
      ohlcvUnsubscribeRef.current = null;
    }

    // Add a delay to allow the layout to settle
    const initTimer = setTimeout(() => {
      setIsLayoutReady(true);

      // Subscribe to OHLCV data for the new symbol
      marketDataWs.initialize();
      const wsMarketType = marketType === "futures" ? "futures" : "spot";
      ohlcvUnsubscribeRef.current = marketDataWs.subscribe<OHLCVData>({
        symbol,
        type: "ohlcv",
        marketType: wsMarketType,
        interval: "1h",
      }, (data) => {
        if (data && data.data && Array.isArray(data.data) && data.data.length > 0) {
          // Reject hardcoded OHLCV data (all closes within tiny range around exactly 50000)
          const closes = (data.data ?? []).map((c: any) => Number(c[4])).filter((n: number) => n > 0);
          if (closes.length >= 3) {
            const allNear50000 = closes.every((c: number) => Math.abs(c - 50000) < 300);
            if (allNear50000) {
              const min = Math.min(...closes);
              const max = Math.max(...closes);
              if (max - min < 200) {
                console.warn('[ChartPanel] Rejected hardcoded OHLCV data');
                return;
              }
            }
          }
          const candles = data.data.map((candle: any) => ({
            time: candle[0],
            timestamp: new Date(candle[0]).toISOString(),
            date: new Date(candle[0]).toLocaleDateString(),
            open: Number(candle[1]),
            high: Number(candle[2]),
            low: Number(candle[3]),
            close: Number(candle[4]),
            volume: Number(candle[5]),
            color: Number(candle[4]) >= Number(candle[1]) ? '#26a69a' : '#ef5350'
          }));
          setCandleData(candles);
          setDataReady(true);
          if (candles.length > 0) {
            setCurrentPrice(candles[candles.length - 1].close);
          }
        }
      });

      // Force window resize events to trigger chart sizing
      const resizeTimer = setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
        window.dispatchEvent(new CustomEvent("chart-resize-requested"));

        // Add another resize event after a short delay to ensure proper sizing
        setTimeout(() => {
          window.dispatchEvent(new Event("resize"));
          window.dispatchEvent(new CustomEvent("chart-resize-requested"));
        }, 100);
      }, 200);

      return () => clearTimeout(resizeTimer);
    }, 300);

    return () => clearTimeout(initTimer);
  }, [symbol, marketType, setCandleData, setDataReady, setCurrentPrice]);

  // Listen for market switching cleanup events
  useEffect(() => {
    const handleMarketSwitchingCleanup = (event: CustomEvent) => {
      const { oldSymbol, newSymbol } = event.detail;

      // Force chart remount by updating key and resetting layout
      setIsLayoutReady(false);
      setChartKey(`chart-${newSymbol}-${Date.now()}`);
      setLocalCurrentPrice(0);

      // Re-enable layout after cleanup
      setTimeout(() => {
        setIsLayoutReady(true);
      }, 100);
    };

    window.addEventListener('market-switching-cleanup', handleMarketSwitchingCleanup as EventListener);

    return () => {
      window.removeEventListener('market-switching-cleanup', handleMarketSwitchingCleanup as EventListener);
    };
  }, []);

  // Listen for chart type changes from chart context and update ChartDataContext
  useEffect(() => {
    const handleChartTypeChange = ((event: CustomEvent) => {
      const newChartType = event.detail.chartType;
      console.log(`[ChartPanel] Chart type changed to: ${newChartType}`);
      setChartType(newChartType);
    }) as EventListener;

    window.addEventListener("chart-type-changed", handleChartTypeChange);

    return () => {
      window.removeEventListener("chart-type-changed", handleChartTypeChange);
    };
  }, [setChartType]);

  // Listen for layout changes and trigger chart resize
  useEffect(() => {
    const handleLayoutChange = () => {
      // Delay the resize to allow layout to settle
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
        window.dispatchEvent(new CustomEvent("chart-resize-requested"));
      }, 100);
    };

    // Listen for panel collapse/expand events
    const handlePanelToggle = () => {
      handleLayoutChange();
    };

    // Add event listeners for panel state changes
    window.addEventListener("panel-collapsed", handlePanelToggle);
    window.addEventListener("panel-expanded", handlePanelToggle);

    return () => {
      window.removeEventListener("panel-collapsed", handlePanelToggle);
      window.removeEventListener("panel-expanded", handlePanelToggle);
    };
  }, []);

  // If no symbol is provided, show an error message
  if (!symbol) {
    return (
      <div className="w-full h-full bg-black flex items-center justify-center text-white">
        <div className="flex flex-col items-center gap-4 p-6 bg-gray-900 rounded-lg max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-red-500" />
          <h3 className="text-xl font-bold">
            {t("no_trading_symbol_selected")}
          </h3>
          <p className="text-gray-400">
            {t("please_select_a_trading_symbol_to_view_the_chart")}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-black relative overflow-hidden">
      {/* Enhanced loading state */}
      {!isLayoutReady && (
        <div className="w-full h-full bg-black flex flex-col items-center justify-center relative">
          {/* Animated background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-black to-zinc-900 opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/10 via-transparent to-purple-900/10 animate-pulse" />

          {/* Loading content */}
          <div className="relative z-10 flex flex-col items-center space-y-4">
            {/* Enhanced spinner */}
            <div className="relative">
              <div className="animate-spin h-12 w-12 border-3 border-blue-500/30 border-t-blue-500 rounded-full"></div>
              <div className="absolute inset-0 animate-spin h-12 w-12 border-3 border-transparent border-r-purple-500/50 rounded-full animation-delay-150"></div>
            </div>

            {/* Loading text */}
            <div className="text-center space-y-2">
              <div className="text-white font-medium text-lg">
                Loading Chart
              </div>
              <div className="text-zinc-400 text-sm">
                Preparing {symbol} data...
              </div>
            </div>
          </div>

          {/* Progress dots */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-zinc-600 rounded-full animate-pulse animation-delay-200"></div>
            <div className="w-2 h-2 bg-zinc-600 rounded-full animate-pulse animation-delay-400"></div>
          </div>
        </div>
      )}

      {/* Chart content */}
      {isLayoutReady && (
        <div className="w-full h-full relative">
          <ChartSwitcher
            key={chartKey}
            symbol={symbol}
            timeFrame={selectedTimeframe}
            onChartContextReady={handleChartContextReady}
            showExpiry={false}
            expiryMinutes={5}
            orders={[]}
            marketType={marketType}
            onPriceUpdate={onPriceUpdate}
            metadata={metadata}
          />

          {/* Chart overlay for better visual hierarchy */}
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/20 via-transparent to-transparent" />
        </div>
      )}
    </div>
  );
}
